/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
the European Commission - subsequent versions of the EUPL (the "Licence");
You may not use this work except in compliance with the Licence.
You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

Unless required by applicable law or agreed to in writing, software
distributed under the Licence is distributed on an "AS IS" basis,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the Licence for the specific language governing permissions and
limitations under the Licence. */

import AccessibleIcon from "@mui/icons-material/Accessible";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NearMeIcon from "@mui/icons-material/NearMe";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useIntl } from "react-intl";
import BusShelter from "../../../../static/icons/facilities/BusShelter";
import { StopPlaceActions } from "../../../../actions";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import AccessibilityQuayTab from "../../../EditStopPage/AccessibilityAssessment/AccessibilityQuayTab";
import FacilitiesQuayTab from "../../../EditStopPage/Facility/FacilitiesQuayTab";
import { CenterMapButton, CopyIdButton, ImportedId } from "../../Shared";
import { QuayPanelProps } from "../types";
import { BoardingPositionsTab } from "./BoardingPositionsTab";
import { KeyValuesTab } from "./KeyValuesTab";
import { StopPlaceBreadcrumb } from "./StopPlaceBreadcrumb";
import { getQuayTitle } from "../../Shared/quayLabels";

/**
 * Full quay editor panel.
 * Tabs mirror the legacy EditQuayAdditional structure:
 *   General | Accessibility | Facilities | Boarding Positions
 *
 * AccessibilityQuayTab and FacilitiesQuayTab are reused from the legacy
 * codebase — they are already Redux-connected and accept quay + index + disabled.
 */
export const QuayPanel: React.FC<QuayPanelProps> = ({
  quayIndex,
  stopPlace,
  canEdit,
  onBack,
  onDelete,
  onPublicCodeChange,
  onPrivateCodeChange,
  onDescriptionChange,
  onCompassBearingChange,
}) => {
  const BOARDING_POSITIONS_TAB = 3;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState(0);

  const focusedBoardingPosition = useAppSelector(
    (state) =>
      (state as any).mapUtils?.focusedBoardingPositionElement as
        { index: number; quayIndex: number } | undefined,
  );

  // Reset to the General tab whenever the panel switches to a different quay,
  // so a tab left open on the previous quay doesn't carry over.
  useLayoutEffect(() => {
    setActiveTab(0);
  }, [quayIndex]);

  /* Keep map focus on the quay this panel is showing. `addElementToStop` reads
   * `mapUtils.focusedElement` to decide which quay a new boarding position
   * attaches to, and the map's add button only offers boarding positions while a
   * quay is focused. The boarding-positions tab used to assert this itself, just
   * before adding; asserting it here means the guarantee survives that button's
   * removal and holds for anything else in the panel that depends on focus. */
  useEffect(() => {
    dispatch(StopPlaceActions.setElementFocus(quayIndex, "quay"));
  }, [dispatch, quayIndex]);

  // Switch to boarding positions tab when a boarding position marker is clicked.
  // Runs after the reset above, so it wins when both fire in the same commit
  // (e.g. clicking a boarding position that belongs to a different quay).
  useEffect(() => {
    if (
      focusedBoardingPosition &&
      focusedBoardingPosition.quayIndex === quayIndex &&
      focusedBoardingPosition.index >= 0
    ) {
      setActiveTab(BOARDING_POSITIONS_TAB);
    }
  }, [focusedBoardingPosition, quayIndex]);

  const quay = stopPlace.quays?.[quayIndex];
  if (!quay) return null;

  const displayCode = getQuayTitle(quay, quayIndex, formatMessage);

  const privateCodeValue =
    typeof quay.privateCode === "object"
      ? quay.privateCode?.value || ""
      : quay.privateCode || "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <StopPlaceBreadcrumb stopPlace={stopPlace} />

      <Divider />

      {/* ── Quay header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1,
          py: 0.5,
          minHeight: 48,
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <Tooltip title={formatMessage({ id: "back" })}>
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <LocationOnIcon sx={{ fontSize: "1.3rem", flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
            {displayCode}
          </Typography>
          {quay.id && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                mt: -0.25,
              }}
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                {quay.id}
              </Typography>
              <CopyIdButton idToCopy={quay.id} size="small" />
            </Box>
          )}
        </Box>
        <CenterMapButton
          location={
            quay.location
              ? ([quay.location[0], quay.location[1]] as [number, number])
              : stopPlace.location
          }
        />
      </Box>

      <Divider />

      {/* ── Tabs ── */}
      <Box sx={{ flexShrink: 0, bgcolor: "background.default" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, py: 0 } }}
        >
          <Tooltip title={formatMessage({ id: "general" })} placement="bottom">
            <Tab icon={<InfoOutlinedIcon fontSize="small" />} value={0} />
          </Tooltip>
          <Tooltip
            title={formatMessage({ id: "accessibility" })}
            placement="bottom"
          >
            <Tab icon={<AccessibleIcon fontSize="small" />} value={1} />
          </Tooltip>
          <Tooltip
            title={formatMessage({ id: "facilities" })}
            placement="bottom"
          >
            <Tab icon={<BusShelter sx={{ fontSize: "1.25rem" }} />} value={2} />
          </Tooltip>
          <Tooltip
            title={formatMessage({ id: "boarding_positions_tab_label" })}
            placement="bottom"
          >
            <Tab icon={<NearMeIcon fontSize="small" />} value={3} />
          </Tooltip>
          <Tooltip
            title={formatMessage({ id: "key_values_hint" })}
            placement="bottom"
          >
            <Tab icon={<VpnKeyIcon fontSize="small" />} value={4} />
          </Tooltip>
        </Tabs>
      </Box>

      <Divider />

      {/* ── Tab content (scrollable) ── */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {/* Tab 0 — General */}
        {activeTab === 0 && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label={formatMessage({ id: "publicCode" })}
                value={quay.publicCode || ""}
                onChange={(e) => onPublicCodeChange(quayIndex, e.target.value)}
                disabled={!canEdit}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label={formatMessage({ id: "privateCode" })}
                value={privateCodeValue}
                onChange={(e) => onPrivateCodeChange(quayIndex, e.target.value)}
                disabled={!canEdit}
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>

            <TextField
              label={formatMessage({ id: "description" })}
              value={quay.description || ""}
              onChange={(e) => onDescriptionChange(quayIndex, e.target.value)}
              disabled={!canEdit}
              size="small"
              fullWidth
            />

            <TextField
              label={formatMessage({ id: "compass_bearing" })}
              type="number"
              value={quay.compassBearing ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                onCompassBearingChange(
                  quayIndex,
                  raw === "" ? null : Number(raw),
                );
              }}
              disabled={!canEdit}
              size="small"
              slotProps={{ htmlInput: { min: 0, max: 360 } }}
              helperText={formatMessage({
                id: "change_compass_bearing_help_text",
              })}
              sx={{ width: "50%" }}
            />

            {quay.importedId && quay.importedId.length > 0 && (
              <ImportedId
                text={formatMessage({ id: "importedId" })}
                id={quay.importedId}
                showCopyButtons
              />
            )}
          </Box>
        )}

        {/* Tab 1 — Accessibility (reuses legacy Redux-connected component) */}
        {activeTab === 1 && (
          <AccessibilityQuayTab
            quay={quay as any}
            index={quayIndex}
            disabled={!canEdit}
          />
        )}

        {/* Tab 2 — Facilities (reuses legacy Redux-connected component) */}
        {activeTab === 2 && (
          <FacilitiesQuayTab
            quay={quay as any}
            index={quayIndex}
            disabled={!canEdit}
          />
        )}

        {/* Tab 3 — Boarding Positions */}
        {activeTab === 3 && (
          <BoardingPositionsTab
            quay={quay}
            quayIndex={quayIndex}
            canEdit={canEdit}
          />
        )}

        {/* Tab 4 — Key Values */}
        {activeTab === 4 && (
          <KeyValuesTab
            keyValues={quay.keyValues || []}
            disabled={!canEdit}
            origin={{ type: "quay", index: quayIndex }}
          />
        )}
      </Box>

      {/* Footer, only with something in it: an empty one left a blank band
          on read-only views (older version, F-96). */}
      {canEdit && (
        <>
          <Divider />
          <Box
            sx={{
              display: "flex",
              gap: 1,
              px: 2,
              py: 1.5,
              bgcolor: "background.paper",
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(quayIndex)}
            >
              {formatMessage({ id: "delete_quay" })}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
